import { useEffect, useState } from "react";
import { toast } from "sonner";
import { CalendarClock, Phone, Mail, X, Loader2, Inbox, CheckCircle2 } from "lucide-react";
import api from "@/lib/api";
import DashLayout from "@/components/xuma/DashLayout";
import { Button } from "@/components/ui/button";

const fmt = (iso) => { try { return new Date(iso).toLocaleString("es-MX", { weekday: "short", day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" }); } catch { return iso; } };
const isFuture = (iso) => new Date(iso) > new Date();

export default function Appointments() {
  const [appts, setAppts] = useState(null);

  const load = () => api.get("/appointments").then(({ data }) => setAppts(data)).catch(() => setAppts([]));
  useEffect(() => { load(); }, []);

  const cancel = async (id) => {
    try {
      await api.put(`/appointments/${id}`);
      setAppts((a) => a.map((x) => (x.appointment_id === id ? { ...x, status: "cancelled" } : x)));
      toast.success("Cita cancelada");
    } catch { toast.error("No se pudo cancelar"); }
  };

  if (appts === null) {
    return <DashLayout title="Citas"><div className="grid place-items-center py-20"><Loader2 className="h-8 w-8 animate-spin text-xuma-primary" /></div></DashLayout>;
  }

  const upcoming = appts.filter((a) => a.status === "scheduled" && isFuture(a.start));
  const past = appts.filter((a) => a.status !== "scheduled" || !isFuture(a.start));

  const Row = ({ a }) => (
    <div data-testid={`appt-${a.appointment_id}`} className={`rounded-2xl bg-white border border-xuma-border p-4 flex items-center justify-between gap-3 ${a.status === "cancelled" ? "opacity-60" : ""}`}>
      <div className="min-w-0">
        <p className="font-medium text-xuma-dark capitalize flex items-center gap-2">
          <CalendarClock className="h-4 w-4 text-xuma-primary" /> {fmt(a.start)}
        </p>
        <p className="text-sm text-xuma-dark mt-1">{a.name}{a.service ? ` · ${a.service}` : ""}</p>
        <div className="flex items-center gap-3 text-xs text-xuma-muted mt-0.5">
          {a.phone && <span className="flex items-center gap-1"><Phone className="h-3 w-3" /> {a.phone}</span>}
          {a.email && <span className="flex items-center gap-1"><Mail className="h-3 w-3" /> {a.email}</span>}
          {a.google_event_id && <span className="text-green-600 flex items-center gap-1"><CheckCircle2 className="h-3 w-3" /> Google</span>}
        </div>
      </div>
      {a.status === "scheduled" ? (
        <Button variant="ghost" size="sm" data-testid={`cancel-appt-${a.appointment_id}`} onClick={() => cancel(a.appointment_id)} className="text-xuma-muted gap-1 shrink-0">
          <X className="h-4 w-4" /> Cancelar
        </Button>
      ) : <span className="text-xs text-red-600 shrink-0">Cancelada</span>}
    </div>
  );

  return (
    <DashLayout title="Citas">
      {appts.length === 0 ? (
        <div className="rounded-3xl bg-white border border-xuma-border p-12 text-center">
          <Inbox className="h-10 w-10 mx-auto text-xuma-muted" />
          <h3 className="mt-4 font-heading font-bold text-xl text-xuma-dark">Aún no tienes citas</h3>
          <p className="mt-2 text-sm text-xuma-muted">Activa la agenda en tu tarjeta para recibir citas de tus visitantes y del asistente.</p>
        </div>
      ) : (
        <div className="space-y-8">
          <div>
            <h2 className="font-heading font-bold text-lg text-xuma-dark mb-3">Próximas ({upcoming.length})</h2>
            <div className="space-y-3">
              {upcoming.length === 0 ? <p className="text-sm text-xuma-muted">Sin citas próximas.</p> : upcoming.map((a) => <Row key={a.appointment_id} a={a} />)}
            </div>
          </div>
          {past.length > 0 && (
            <div>
              <h2 className="font-heading font-bold text-lg text-xuma-dark mb-3">Historial</h2>
              <div className="space-y-3">{past.map((a) => <Row key={a.appointment_id} a={a} />)}</div>
            </div>
          )}
        </div>
      )}
    </DashLayout>
  );
}
